import { loadStyle } from '../utils/css.js'

const DENSITY_CSS = `
[data-ae-density="compact"] {
  --ae-field-py: 0.25rem;
  --ae-field-px: 0.5rem;
  --ae-form-gap: 0.4rem;
  --ae-label-size: 0.8125rem;
}
[data-ae-density="cozy"] {
  --ae-field-py: 0.4rem;
  --ae-field-px: 0.7rem;
  --ae-form-gap: 0.65rem;
  --ae-label-size: 0.84rem;
}
[data-ae-density="comfortable"] {
  --ae-field-py: 0.625rem;
  --ae-field-px: 0.875rem;
  --ae-form-gap: 0.875rem;
  --ae-label-size: 0.875rem;
}
`

type Tier = 'compact' | 'cozy' | 'comfortable'

interface DensityOptions {
  /** Stable cache key for localStorage — usually the component type or instance id */
  cacheKey?: string
  /** Explicit user preference; when set, wins over measurement */
  pref?: Tier
  /** Legacy responsive mode; density is fixed unless this is explicitly enabled */
  auto?: boolean
  /** Lower breakpoint (px) — below this is compact */
  lo?: number
  /** Upper breakpoint (px) — above this is comfortable */
  hi?: number
  /** Hysteresis margin (px) */
  margin?: number
}

const DEFAULT_LO = 640
const DEFAULT_HI = 960
const DEFAULT_MARGIN = 24

function storeKey(cacheKey: string) {
  return `ae-density:${cacheKey}`
}

function tierFor(width: number, prev: Tier, lo: number, hi: number, margin: number): Tier {
  if (prev === 'compact') return width > lo + margin ? (width > hi + margin ? 'comfortable' : 'cozy') : 'compact'
  if (prev === 'comfortable') return width < hi - margin ? (width < lo - margin ? 'compact' : 'cozy') : 'comfortable'
  if (width < lo - margin) return 'compact'
  if (width > hi + margin) return 'comfortable'
  return 'cozy'
}

function resolve(width: number, current: Tier, opts: DensityOptions): Tier {
  if (opts.pref) return opts.pref
  const lo = opts.lo ?? DEFAULT_LO
  const hi = opts.hi ?? DEFAULT_HI
  const margin = opts.margin ?? DEFAULT_MARGIN
  return tierFor(width, current, lo, hi, margin)
}

function applyDensity(el: HTMLElement, tier: Tier) {
  if (el.getAttribute('data-ae-density') !== tier) {
    el.setAttribute('data-ae-density', tier)
  }
}

function primeFromCache(el: HTMLElement, opts: DensityOptions): Tier {
  if (opts.pref) return opts.pref

  const key = opts.cacheKey
  if (!key) return 'cozy'

  try {
    const cached = localStorage.getItem(storeKey(key))
    if (cached === 'compact' || cached === 'cozy' || cached === 'comfortable') {
      return cached
    }
  } catch { /* localStorage unavailable */ }

  return 'cozy'
}

function parseBinding(value: any, arg: string): DensityOptions {
  if (!value && !arg) return {}
  if (!value && arg) return { cacheKey: arg }
  if (typeof value === 'string') return { cacheKey: value }
  const opts = value as DensityOptions
  if (!opts.cacheKey && arg) opts.cacheKey = arg
  return opts
}

let stylesLoaded = false

export default {
  install(Vue) {
    if (!stylesLoaded) {
      loadStyle(DENSITY_CSS, 'ae-density', 'ae-root', 0)
      stylesLoaded = true
    }

    Vue.directive('density', {
      bind(el: any, binding: any) {
        const opts = parseBinding(binding.value, binding.arg)
        const initial = primeFromCache(el, opts)
        applyDensity(el, initial)

        el.___aeDensityCurrent = initial

        if (opts.pref && opts.cacheKey) {
          try { localStorage.setItem(storeKey(opts.cacheKey), opts.pref) } catch {}
        }

        if (!opts.auto) return

        el.___aeDensityObserver = new ResizeObserver(([entry]) => {
          const w = entry.contentRect.width
          const current: Tier = el.___aeDensityCurrent || 'cozy'
          const tier = resolve(w, current, opts)

          if (tier !== current) {
            el.___aeDensityCurrent = tier
            applyDensity(el, tier)
          }

          if (opts.cacheKey) {
            try { localStorage.setItem(storeKey(opts.cacheKey), tier) } catch {}
          }
        })

        el.___aeDensityObserver.observe(el)
      },

      update(el: any, binding: any) {
        const opts = parseBinding(binding.value, binding.arg)

        if (opts.pref) {
          applyDensity(el, opts.pref)
          el.___aeDensityCurrent = opts.pref
          if (opts.cacheKey) {
            try { localStorage.setItem(storeKey(opts.cacheKey), opts.pref) } catch {}
          }

          // If pinned, disconnect observer
          if (el.___aeDensityObserver) {
            el.___aeDensityObserver.disconnect()
            el.___aeDensityObserver = null
          }
        } else if (!opts.auto && el.___aeDensityObserver) {
          el.___aeDensityObserver.disconnect()
          el.___aeDensityObserver = null
        }
      },

      unbind(el: any) {
        if (el.___aeDensityObserver) {
          el.___aeDensityObserver.disconnect()
          el.___aeDensityObserver = null
        }
        delete el.___aeDensityCurrent
      }
    })
  }
}
